import { getRenderTheme, mainRenderer } from './use-canvas-render';
import { useLayerStore } from '@/store/use-layer-store';
import { WatchfaceRenderer } from '@/watch-renderer';
import { Ref, ref } from 'vue';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { RemoteLibsService } from '@/services/remote-libs.service';
import { getRecordDataSource } from '@/resources/data-source-record';
import { dataSourceWholeItems } from '@/resources/data-source';
import { isSourceKeyFormat } from '@/utils/number';
import { buildAst } from '@/utils/ast';
import {
  getImageAndVideoDoms,
  getThemeRive,
  getWatchfaceDeviceRadius
} from '.';
import { useDataSourceStore } from '@/store/use-data-source-store';
import {
  frameImagesToAnimateWebp,
  mediaBlobToAnimateWebp
} from '@/utils/ffmpeg.webp';
import { clipRoundImg } from '@/utils/transform-image';
import { ElectronApi } from '@/services/electron.api';
import { canUse } from '@/utils/can-use.utils';

/**
 * 表盘动画
 * @param canvasRef
 * @returns
 */
export const useWatchfaceAnimation = (canvasRef: Ref<HTMLCanvasElement>) => {
  const layerStore = useLayerStore();
  const watchfaceStore = useWatchfaceStore();
  const dataSourceStore = useDataSourceStore();

  let _renderer: WatchfaceRenderer | undefined;
  let _currentTheme: StoreThemeItem | undefined;
  const spriteIds = ref<string[]>([]);

  const _buildAst = (themeName: string, themeType: string) => {
    const ast = buildAst({
      buildFor: 'editor',
      themeName: themeName ?? '',
      themeType: themeType ?? ''
    });
    if (!ast) return;

    // 处理ast
    const resourceAst = ast.children.find((item) => item.tag == 'Resources');

    // 处理数据源最大和最小值
    const _handleValueRange = () => {
      resourceAst?.children.forEach((item) => {
        // 将指针中绑定的最大值和最小值设置为数值
        if (item.tag == 'DataItemPointer') {
          const { valueStart, valueRange } = item.attrsMap;
          const start = isSourceKeyFormat(valueStart)
            ? Number(
                dataSourceWholeItems.find((item) => item.key == valueStart)
                  ?.defaultValue ?? 20
              )
            : Number(valueStart);
          item.attrsMap.valueStart = String(start);

          if (isSourceKeyFormat(valueRange)) {
            const range = Number(
              dataSourceWholeItems.find((item) => item.key == valueRange)
                ?.defaultValue ?? 100
            );
            item.attrsMap.valueRange = String(range - start);
          }
        }
      });
    };
    _handleValueRange();
    return ast;
  };

  const _setSpriteAst = (
    wathfaceAst: Ast,
    videoEls: Record<string, HTMLVideoElement>
  ) => {
    const resourceAst = wathfaceAst.children.find(
      (item) => item.tag == 'Resources'
    );

    // 获取动效总时长
    const aniTotalTime = _getRecordSourceItems().totalTime;
    const spriteAsts = resourceAst?.children.filter(
      (item) => item.tag == 'Sprite'
    );

    // 重置repeatCount，使其总时间等于动效总时长
    spriteAsts?.forEach((spriteAst) => {
      const fileRef = spriteAst.attrsMap.refExt ?? spriteAst.attrsMap.ref;
      const refAst = resourceAst?.children.find(
        (item) => '@' + item.attrsMap.name == fileRef
      );
      let _preCountTime = 1000;
      if (refAst?.tag == 'File') {
        // 视频
        const videoName = refAst.attrsMap.fileName;
        const videoEl = videoEls[videoName];
        if (!videoEl) return;
        _preCountTime = videoEl.duration * 1000;
      } else {
        // 序列帧
        const { interval = 0, ref } = spriteAst.attrsMap;
        // 修改_defaultIndex为0
        // spriteAst.attrsMap._defaultIndex = '0';

        // 获取该sprite总时间
        _preCountTime = (refAst?.children?.length ?? 0) * Number(interval);
      }
      // 如果是重复播放，需要重置repeatCount
      const repeatCount = Number(spriteAst.attrsMap.repeatCount);
      const _repeatCount = Math.max(
        Math.floor(aniTotalTime / _preCountTime),
        1
      );
      if (repeatCount == 0) {
        spriteAst.attrsMap.repeatCount = String(_repeatCount);
      } else {
        spriteAst.attrsMap.repeatCount = String(
          Math.min(_repeatCount, repeatCount)
        );
      }
    });
  };

  const _getRecordSourceItems = () => {
    const useingKeys = Object.keys(_renderer?.runtime.configData ?? {});
    // 如果没有使用数据源，则使用添加一个秒针
    if (!useingKeys.length) {
      useingKeys.push('timeSecond');
    }
    const items = getRecordDataSource(useingKeys);

    // 获取IFTTT条件中使用的数据源，生成能逐一触发各条件的动画数据
    const iftttSourceItems = _getIftttSourceItems();
    if (iftttSourceItems.length) {
      for (const iftttItem of iftttSourceItems) {
        const existingIndex = items.findIndex(
          (item) => item.sourcekey === iftttItem.sourcekey
        );
        if (existingIndex >= 0) {
          // 常规序列已包含该数据源的完整遍历（默认值→最大值→最小值→默认值），
          // IFTTT条件在遍历过程中自然会被触发和取消，无需额外追加IFTTT值序列
        } else {
          // 常规序列中没有该数据源，需要追加IFTTT值序列来触发条件
          items.push(iftttItem);
        }
      }
    }

    return {
      items,
      totalTime: Math.max(
        ...items.map((item) => item.values.length * item.interval)
      )
    };
  };

  /**
   * 从当前主题的IFTTT条件中提取数据源，生成逐一触发各条件的动画数据
   * IFTTT触发式动效展示规则：从条件1到条件N逐一展示，速度同序列帧，默认播放一次
   *
   * 生成的值序列格式：[非触发值, 触发值1, 非触发值, 触发值2, 非触发值, ..., 触发值N, 非触发值]
   * 这样可以在预览中看到每个条件逐一被触发的效果
   */
  const _getIftttSourceItems = () => {
    const theme = _currentTheme;
    if (!theme?.ifttts?.length) return [];

    // 仅亮屏(normal)主题支持IFTTT动效预览，AOD不生成动效预览图
    if (theme.type !== 'normal') return [];

    // 检查设备是否支持IFTTT亮屏动效预览
    if (!canUse('animation-preview').valid) return [];

    // 收集每个IFTTT条件对应的数据源key和触发值
    // 使用数组保持条件顺序，同一个sourceKey可能被多个条件使用
    const sourceKeyTriggerMap: Map<
      string,
      { triggerValue: number; nonTriggerValue: number }[]
    > = new Map();

    for (const ifttt of theme.ifttts) {
      const { condition, animations } = ifttt;
      // 仅处理有show类型动画的IFTTT
      const hasShowAnimation = animations.some((ani) => ani.type === 'show');
      if (!hasShowAnimation) continue;
      if (!condition?.children?.length) continue;

      for (const compare of condition.children) {
        // 支持IFTTT的设备都去掉"点击"触发类型
        if (compare.type !== 'when' || !compare.children?.length) continue;
        for (const param of compare.children) {
          const { whenSourceKey, whenOperator, whenValue } = param;
          if (!whenSourceKey || whenValue === undefined) continue;

          const numValue = Number(whenValue);
          if (isNaN(numValue)) continue;

          // 根据操作符计算能触发条件的值和不能触发条件的值
          let triggerValue: number;
          let nonTriggerValue: number;
          switch (whenOperator) {
            case 'MoreThan':
              // source > value → 触发值取value+1，非触发值取value-1
              triggerValue = numValue + 1;
              nonTriggerValue = Math.max(numValue - 1, 0);
              break;
            case 'MoreThanOrEqual':
              // source >= value → 触发值取value，非触发值取value-1
              triggerValue = numValue;
              nonTriggerValue = Math.max(numValue - 1, 0);
              break;
            case 'LessThan':
              // source < value → 触发值取value-1，非触发值取value+1
              triggerValue = Math.max(numValue - 1, 0);
              nonTriggerValue = numValue + 1;
              break;
            case 'LessThanOrEqual':
              // source <= value → 触发值取value，非触发值取value+1
              triggerValue = numValue;
              nonTriggerValue = numValue + 1;
              break;
            case 'Equal':
              // source == value → 触发值取value，非触发值取value+1
              triggerValue = numValue;
              nonTriggerValue = numValue + 1;
              break;
            default:
              triggerValue = numValue;
              nonTriggerValue = 0;
          }

          if (!sourceKeyTriggerMap.has(whenSourceKey)) {
            sourceKeyTriggerMap.set(whenSourceKey, []);
          }
          sourceKeyTriggerMap.get(whenSourceKey)?.push({
            triggerValue,
            nonTriggerValue
          });
        }
      }
    }

    if (!sourceKeyTriggerMap.size) return [];

    // 为每个数据源生成动画值序列：逐一触发各条件
    const result: {
      sourcekey: string;
      values: (string | number)[];
      interval: number;
    }[] = [];

    // 动效总时长与常规数据源动画保持一致（3200ms）
    const totalTime = 3200;

    for (const [sourceKey, triggers] of sourceKeyTriggerMap) {
      const values: number[] = [];
      const nonTriggerVal = triggers[0]?.nonTriggerValue ?? 0;

      // 逐一添加每个触发值，中间插入非触发值（不在开头插入nonTriggerVal，避免与常规序列末尾的defaultValue形成跳动）
      for (let i = 0; i < triggers.length; i++) {
        values.push(triggers[i].triggerValue);
        values.push(nonTriggerVal);
      }

      // 末尾多加一帧防止最后一帧闪烁（与getRecordDataSource保持一致）
      values.push(values[values.length - 1]);

      // 计算interval使总时长匹配
      const interval = Math.ceil(totalTime / values.length);

      result.push({
        sourcekey: sourceKey,
        values,
        interval
      });
    }

    return result;
  };

  const initCanvas = async (
    theme: StoreThemeItem,
    colorItem?: StoreThemeColorItem
  ) => {
    const canvas = canvasRef.value;
    const options = mainRenderer?.options;
    if (!canvas || !options || !mainRenderer) return;

    _currentTheme = theme;

    if (options.configData !== undefined) {
      options.configData = dataSourceStore.wholeValues(undefined);
    }
    const ast = _buildAst(theme.name, theme.type);
    if (!ast) throw new Error('ast is undefined');

    _renderer =
      _renderer ??
      new WatchfaceRenderer(canvas, {
        ...options
      });
    if (!_renderer) throw new Error('renderer is undefined');

    const rive = await getThemeRive({
      theme,
      wathchOS: watchfaceStore.description.watchOS,
      // 动效时不使用全局的rive, 因为在做动效时，画布也会变动
      renderer: null,
      autoLoad: true
    });

    // 获取需要的图片dom和视频video dom
    spriteIds.value = layerStore.getSpriteAndVideoWatchfaceIds(theme.id);
    const { images, videos } = await getImageAndVideoDoms({
      watchfaceAst: ast,
      wholeFrameIds: [...spriteIds.value],
      colorId: colorItem?.id ?? '',
      themeId: theme.id
    });

    // 处理序列帧播放时长
    _setSpriteAst(ast, videos);

    const _options: WatchRendererOptions = {
      ...options,
      ast,
      scale: 1,
      unAssociateTime: true,
      rive: rive,
      forceShowHidden: true,
      clipRound: getWatchfaceDeviceRadius(watchfaceStore.deviceType),
      ignoreBg: false,
      renderTheme: getRenderTheme({
        theme,
        colorMode: watchfaceStore.colorMode,
        colorItem,
        themeIndex: layerStore.themes
          .filter((t) => t.type === 'normal')
          .findIndex((item) => item.id == theme.id)
      }),
      videos,
      images
    };

    _renderer.reset(_options);

    _currentTheme = theme;

    _renderer.renderring();

    return _options;
  };

  const status = ref<'recording' | 'ffmpegDownloading' | 'toWebping' | 'done'>(
    'done'
  );

  const _resetAnimation = () => {
    // 播放之前需要重置动画数据
    // 设置需要做动效的序列帧
    _renderer?.runtime.resetFrameAnimation([...spriteIds.value]);

    // 动效数据源
    const dataSourceItems = _getRecordSourceItems().items;
    _renderer?.runtime.resetSourceAnimation(dataSourceItems);
  };

  /**
   * 表盘播放：数据源播放，序列帧动画播放
   * @returns
   */
  const playAnimation = async (options?: {
    recording: boolean;
    method?: 'frames' | 'MediaRecorder';
  }) => {
    const { recording = false, method = 'frames' } = options ?? {};

    const rednerer = _renderer;
    if (!rednerer) return;

    _resetAnimation();

    const canvas = rednerer.canvas;
    const frameDataURIs: string[] = [];
    let frameDataURIsCount = 0;

    // media recorder
    const allChunks: Blob[] = [];
    const format = 'video/webm;codecs=h264';
    const stream = canvas.captureStream(60); // 录制帧率60fps
    const recorder =
      method == 'MediaRecorder'
        ? new MediaRecorder(stream, {
            mimeType: format
          })
        : undefined;

    const start = Date.now();
    let end = start;

    if (recorder) {
      recorder.ondataavailable = (e) => {
        allChunks.push(e.data);
      };
      recorder.start();
    }

    rednerer.tick({
      frameRendered: () => {
        if (recording) {
          if (method === 'frames') {
            frameDataURIsCount++;
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  const reader = new FileReader();
                  reader.onload = () => {
                    frameDataURIs.push(reader.result as string);
                  };
                  reader.readAsDataURL(blob);
                }
              },
              'image/webp',
              0.9
            );
          }
        }
      },
      completed: () => {
        end = Date.now();
        if (recorder) recorder.stop();
      }
      // ignoreAniTypes: ['frame']
    });

    return new Promise<{
      duration: number;
      frameURIs: string[];
      blob: Blob;
    }>((resolve) => {
      const timer = window.setInterval(() => {
        if (
          !rednerer.tickTimer &&
          (method === 'frames'
            ? frameDataURIsCount <= frameDataURIs.length
            : true)
        ) {
          resolve({
            duration: end - start,
            frameURIs: frameDataURIs,
            blob: new Blob(allChunks)
          });
          window.clearInterval(timer);
        }
      }, 0);
    });
  };

  /**
   * 导出预览图
   * @param webpName 预览图保存的文件名
   * @returns
   */
  const exportWebp = async (webpName?: string) => {
    const method = 'frames';
    try {
      // 先播放
      status.value = 'recording';

      const res = await playAnimation({
        recording: true,
        method: method
      });

      if (!res) return;

      const { duration, blob, frameURIs } = res;
      console.log('res', res);

      status.value = 'toWebping';
      // 首先下载ffmeg
      const service = new RemoteLibsService();
      if (!(await service.exists('ffmpeg'))) {
        status.value = 'ffmpegDownloading';
        await service.getLibFilename('ffmpeg');
      }

      let webp: undefined | string = '';
      if (method == 'frames') {
        // 逐帧像素级圆角裁剪，圆角外设为透明
        // radius 比 canvas clip 的圆角小1px，确保 clip 抗锯齿的半透明边缘像素被裁掉
        const deviceType = watchfaceStore.deviceType;
        const radius = Math.max(getWatchfaceDeviceRadius(deviceType) - 1, 0);
        const bgColor = _renderer?.runtime.bgColor || '#000000';

        // 先尝试透明裁剪 + alpha通道编码（效果最好）
        const transparentFrames: string[] = [];
        for (const frame of frameURIs) {
          const clipped = await clipRoundImg(frame, radius);
          transparentFrames.push(clipped || frame);
        }
        webp = await frameImagesToAnimateWebp({
          frameURIs: transparentFrames,
          duration,
          useAlpha: true
        });

        // 如果alpha编码失败（ffmpeg不支持），回退到bgColor填充
        if (!webp) {
          const bgFrames: string[] = [];
          for (const frame of frameURIs) {
            const clipped = await clipRoundImg(frame, radius, bgColor);
            bgFrames.push(clipped || frame);
          }
          webp = await frameImagesToAnimateWebp({
            frameURIs: bgFrames,
            duration
          });
        }
      } else if (method === 'MediaRecorder') {
        const deviceType = watchfaceStore.deviceType;
        const { width, height } = watchfaceStore.size;
        const radius = getWatchfaceDeviceRadius(deviceType);
        // const maskDataURI = getRoundRectangle({ width, height, radius });
        // if (!maskDataURI) throw 'maskDataURI is undefined';
        webp = await mediaBlobToAnimateWebp({
          webmBlob: blob,
          width,
          height,
          radius
        });
      }

      if (!webp) throw new Error('webp is undefined');

      // 将webbp文件保存到表盘目录下
      const { fs } = ElectronApi;
      const to = webpName ?? webp;
      if (to != webp) await fs?.promises.cp(webp, to);

      status.value = 'done';
      return to;
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      status.value = 'done';
    }
  };

  return { playAnimation, status, exportWebp, initCanvas, renderer: _renderer };
};
